import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Link from "@mui/material/Link";
import { grey } from "designSystem/styles/color";
import { useLocale } from "designSystem/locale";
import React from "react";
import * as api from "../api";
import LoadingSlack from "./LoadingSlack";
import MessageGroup from "./MessageGroup";
import SlackContext from "./SlackContext";
import { useStore } from "csi/state/hooks";

const PREFIX = "ConversationHistory";

const classes = {
	root: `${PREFIX}root`,
	refresh: `${PREFIX}refresh`,
	refreshLink: `${PREFIX}refreshLink`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	[`&.${classes.root}`]: {
		flexWrap: "nowrap",
		justifyContent: "flex-start",
		height: "100%",
		overflowX: "hidden",
		overflowY: "scroll",
		width: "100%",
	},

	[`& .${classes.refresh}`]: {
		alignSelf: "center",
		color: grey[900],
		fontSize: "12px",
		lineHeight: "15px",
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingBottom: theme.spacing(3),

	},

	[`& .${classes.refreshLink}`]: {
		display: "inline",
	},
}));

export interface Props {
	channelId: string;
	history: api.ConversationHistory;
	users: api.UsersList;
	lastUpdate?: Date;
	onRefresh?: () => void;
}

export const ConversationHistory: React.FunctionComponent<Props> = ({ channelId, history, lastUpdate, onRefresh, users }) => {
	if (history == null || users == null || lastUpdate == null) {
		return <LoadingSlack />;
	}
	const locale = useLocale();

	const { sessionStore } = useStore();

	const usersMap = users.members.reduce((map, user) => {
		// user itself
		map[user.id] = user;

		// bot user
		const botId = user.profile?.botId;
		if (botId) {
			map[botId] = user;
		}
		return map;
	}, {});

	const dateFormatOptions: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", hour: "numeric", minute: "numeric" };
	const updated = lastUpdate?.toLocaleDateString(undefined, dateFormatOptions);
	const groups = api.groupMessagesByDate(history.messages);

	const messageGroups = groups.map((group) => (
		<MessageGroup
			channelId={channelId}
			group={group}
			key={group.dateAsString}
		/>
	));
	return (
		<StyledGrid
			className={classes.root}
			container
			direction="column-reverse"
		>
			{/* Because of reverse order refresh button comes first */
				sessionStore.debugMode ? (
					<Grid className={classes.refresh} item>
						{updated ? <span>{locale.resource("LastUpdated", [updated])} </span> : null}
						{onRefresh ? <Link className={classes.refreshLink} onClick={(ev) => { ev.preventDefault(); onRefresh(); }}>{locale.resource("Refresh")}</Link> : null}
					</Grid>
				) : null
			}
			<SlackContext.Provider value={{ usersMap }}>
				{messageGroups}
			</SlackContext.Provider>
		</StyledGrid>
	);
};

export default ConversationHistory;